import React from "react";
import {
  Box,
  Button,
  Grid,
  Link,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { COLORS } from "../../constants";
import DOMPurify from "dompurify";
import useSystemHook from "../../hooks/SystemHook";

const ProductTabs = ({
  tabValue,
  handleTabChange,
  product,
  applicationMakes,
  applicationColumns,
  numberOfRows,
  handleApplicationFilterClick,
  applicationFilters,
  authError,
}) => {
  const sanitizedMarkup = (html) => ({
    __html: DOMPurify.sanitize(html || ""),
  });
  const { isMobile, isTablet, isDesktop } = useSystemHook();

  // Shared Tab Content (used by both mobile & desktop)
  const TabContent = () => (
    <>
      {tabValue === "description" && (
        <Typography
          sx={{ fontSize: isMobile ? "var(--font-size-xxs)" : "inherit" }}
        >
          {product?.productDescription}
        </Typography>
      )}

      {tabValue === "application" &&
        (authError ? (
          <Grid container justifyContent="center" sx={{ my: 4 }}>
            <Typography variant="body1" color="text.secondary" align="center">
              Please{" "}
              <Link href="/login" underline="hover" sx={{ fontWeight: 500 }}>
                log in
              </Link>{" "}
              or{" "}
              <Link href="/register" underline="hover" sx={{ fontWeight: 500 }}>
                create an account
              </Link>{" "}
              to view vehicle applications.
            </Typography>
          </Grid>
        ) : (
          <Box
            sx={{
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.border}`,
              borderTop: "none",
            }}
          >
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        borderRight: "1px solid #e0e0e0",
                        fontSize: "var(--font-size-xxs)",
                      }}
                    >
                      MAKE
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        borderRight: "1px solid #e0e0e0",
                        fontSize: "var(--font-size-xxs)",
                      }}
                    >
                      MODEL
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        borderRight: "1px solid #e0e0e0",
                        fontSize: "var(--font-size-xxs)",
                      }}
                    >
                      VARIANT
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>YEAR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: numberOfRows }).map((_, index) => (
                    <TableRow key={index} hover>
                      {applicationColumns.map(
                        ({ key, data, filterType }, colIndex) => {
                            const dal = data[index];
                            const dalId = dal?.makeId ?? dal?.modelId ?? dal?.variantId;

                            return <TableCell
                                key={key}
                                sx={{
                                    borderBottom: "none",
                                    borderRight:
                                        colIndex < 3 ? "1px solid #e0e0e0" : "none",
                                    py: 1,
                                    fontSize: isMobile
                                        ? "var(--font-size-xxs)"
                                        : "inherit",
                                }}
                            >
                                {data && data[index]?.name ? (
                                    filterType ? (
                                        <Link
                                            component="button"
                                            variant="body2"
                                            underline="hover"
                                            onClick={() =>
                                                applicationFilters[filterType] !== dalId &&
                                                handleApplicationFilterClick(filterType, dal)
                                            }
                                            sx={{
                                                color:
                                                    applicationFilters[filterType] === dalId
                                                        ? "text.primary"
                                                        : "primary.main",
                                                fontWeight:
                                                    applicationFilters[filterType] === dalId
                                                        ? "bold"
                                                        : "normal",
                                                pointerEvents:
                                                    applicationFilters[filterType] === dalId
                                                        ? "none"
                                                        : "auto",
                                            }}
                                        >
                                            {data[index]?.name}
                                        </Link>
                                    ) : (
                                        <Typography variant="body2">
                                            {data[index]?.name}
                                        </Typography>
                                    )
                                ) : (
                                    "—"
                                )}
                            </TableCell>;
                        }
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

      {tabValue === "warranty" && (
        <Typography
          sx={{ fontSize: isMobile ? "var(--font-size-xxs)" : "inherit" }}
        >
          {product?.warranty} <br /> {product?.warrantyText || ""}
        </Typography>
      )}
    </>
  );

  // Desktop Version
  if (!isMobile) {
    return (
      <>
        <Box sx={{ mt: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="product tabs"
            sx={{
              "& .MuiTabs-flexContainer": { gap: 1 },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            <Tab
              label="DESCRIPTION"
              value="description"
              sx={{
                backgroundColor:
                  tabValue === "description" ? COLORS.white : COLORS.border,
                color:
                  tabValue === "description"
                    ? COLORS.darkText
                    : COLORS.lightText,
                borderTop:
                  tabValue === "description"
                    ? `2px solid ${COLORS.themeRed}`
                    : "none",
                "&:hover": { opacity: 0.8 },
              }}
            />
            <Tab
              label="APPLICATION"
              value="application"
              sx={{
                backgroundColor:
                  tabValue === "application" ? COLORS.white : COLORS.border,
                color:
                  tabValue === "application"
                    ? COLORS.darkText
                    : COLORS.lightText,
                borderTop:
                  tabValue === "application"
                    ? `2px solid ${COLORS.themeRed}`
                    : "none",
                "&:hover": { opacity: 0.8 },
              }}
            />
            <Tab
              label="WARRANTY"
              value="warranty"
              sx={{
                backgroundColor:
                  tabValue === "warranty" ? COLORS.white : COLORS.border,
                color:
                  tabValue === "warranty" ? COLORS.darkText : COLORS.lightText,
                borderTop:
                  tabValue === "warranty"
                    ? `2px solid ${COLORS.themeRed}`
                    : "none",
                "&:hover": { opacity: 0.8 },
              }}
            />
          </Tabs>
        </Box>

        <Box
          sx={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "2px",
            p: 2,
            mt: 2,
            color: COLORS.lightText,
            fontWeight: 300,
          }}
        >
          <TabContent />
        </Box>
      </>
    );
  }

  // Mobile Version
  return (
    <>
      <Box sx={{ mt: 5 }}>
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: 1,
            py: 1,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {[
            { label: "DESCRIPTION", value: "description" },
            { label: "APPLICATION", value: "application" },
            { label: "WARRANTY", value: "warranty" },
          ].map((tab) => (
            <Button
              key={tab.value}
              onClick={() => handleTabChange(null, tab.value)}
              sx={{
                minWidth: "120px",
                px: 2,
                py: 1,
                fontSize: "var(--font-size-xxs)",
                fontWeight: 500,
                textTransform: "uppercase",
                backgroundColor:
                  tabValue === tab.value ? COLORS.white : COLORS.border,
                color:
                  tabValue === tab.value ? COLORS.darkText : COLORS.lightText,
                borderTop:
                  tabValue === tab.value
                    ? `2px solid ${COLORS.themeRed}`
                    : "none",
                borderRadius: 0,
                flexShrink: 0,
                "&:hover": { opacity: 0.8 },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: COLORS.white,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "2px",
          p: 2,
          mt: 2,
          color: COLORS.lightText,
          fontWeight: 300,
        }}
      >
        <TabContent />
      </Box>
    </>
  );
};

export default ProductTabs;
